import {FC, useEffect, useMemo, useState} from "react";
import { StorySize } from '@/moment/types/story-types';
import { useTheme } from "@/moment/hooks/useTheme";
import "./PublisherLogo.scss";

interface PublisherLogoProps {
  publisherLogo: string;
  publisherName: string;
  storySize?: StorySize;
  darkPublisherLogo?: string;
  isDark?: boolean;
}

const PublisherLogo: FC<PublisherLogoProps> = ({publisherLogo, publisherName, storySize, isDark, darkPublisherLogo}) => {

  const { theme } = useTheme();
  const [isHydrated, setIsHydrated] = useState(false);

  // Prevent hydration mismatch by only rendering after hydration
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const getClassName = useMemo(() => {
    switch (storySize) {
      case StorySize.CAROUSEL:
        return 'story-pub-logo-carousel';
      case StorySize.LARGE:
        return 'story-pub-logo-lg';
      case StorySize.MEDIUM:
        return 'story-pub-logo-md';
      case StorySize.SMALL:
        return 'story-pub-logo-sm';
      default:
        return 'story-pub-logo-md';
    }
  }, [storySize]);

  const displayLogo = useMemo(() => {
    // During SSR or before hydration, use a consistent default
    if (!isHydrated) {
      // Use the regular logo as default during SSR to avoid hydration mismatch
      return publisherLogo || '';
    }

    // After hydration, use the actual theme logic
    const shouldUseDarkLogo = isDark !== undefined ? isDark : theme === 'dark';
    
    if (shouldUseDarkLogo) {
      // In dark mode, show the regular logo (designed for dark backgrounds)
      return publisherLogo || '';
    } else {
      // In light mode, show the dark logo (designed for light backgrounds)
      return darkPublisherLogo || publisherLogo || '';
    }
  }, [theme, isDark, publisherLogo, darkPublisherLogo, isHydrated]);

  return (
    <div className={`story-pub-logo ${getClassName}`}>
        {displayLogo !== '' ? <img src={displayLogo} alt={publisherName} test-notes={isDark? 'dark' : 'light'} className="story-pub-logo-img" /> : ''}
    </div>
  );
};

export default PublisherLogo;
